import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { useKeycloak } from "@react-keycloak/web";
import { authenticatedGet } from "../../api/authenticatedFetch.js";
import { widgetRegistry, KPIWidget, TableWidget, AreaChartWidget, BarChartWidget, DonutChartWidget, SectionBreakWidget, ForecastChartWidget, ForecastTableWidget, ResourceChangesTrendWidget, ResourceChangesOverviewWidget, ResourceListWidget, ResourceOverviewWidget, ResourceUtilizationWidget, InsightTableWidget, CostTrendChartWidget } from "../../components/widgets/index.js";
import { getDefaultSizes } from "../../components/widgets/widgetConfigSchema.js";
import { useFilterStoreWithDayjsDates } from "../../store/filterStore.js";
import logoWide from "../../assets/logo_wide.png";
import "./ReportPrint.css";

// Widget types that should always be full width in print mode
// Charts, tables, and section breaks get their own row
// KPIs can be positioned next to each other
const FULL_WIDTH_WIDGET_TYPES = new Set([
    'areachart',
    'barchart',
    'donutchart',
    'forecastchart',
    'forecasttable',
    'costtrendchart',
    'resourcechangestrend',
    'resourcechangesoverview',
    'resourceutilization',
    'table',
    'insighttable',
    'resourcelist',
    'resourceoverview',
    'sectionbreak'
]);

// Check if a widget type should be full width. Module scope: it depends on nothing
// in the component, so processWidgetsForPrint below can memoize without it as a dep.
const isFullWidthWidget = (widgetType) => FULL_WIDTH_WIDGET_TYPES.has(widgetType);


/**
 * ReportPrint - A clean report template for PDF generation
 * 
 * This component renders only the report widgets without any navigation elements:
 * - No sidebar
 * - No header/tabs
 * - No search box
 * - No edit controls
 * 
 * Used by the PDF generator to create clean PDF reports.
 */
const ReportPrint = () => {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const { keycloak } = useKeycloak();
    const { startDate, endDate } = useFilterStoreWithDayjsDates();
    
    // Parse view filters from URL parameters
    const viewFiltersFromURL = useMemo(() => {
        const viewFiltersParam = searchParams.get('view_filters');
        if (!viewFiltersParam) {
            console.log('ReportPrint: No view_filters parameter found in URL');
            return [];
        }
        try {
            // The parameter might be double-encoded, so try decoding twice if needed
            let decoded = decodeURIComponent(viewFiltersParam);
            try {
                // Try parsing first
                const parsed = JSON.parse(decoded);
                console.log('ReportPrint: Parsed view filters from URL:', parsed);
                return parsed;
            } catch (e1) {
                // If that fails, try decoding again (in case of double encoding)
                decoded = decodeURIComponent(decoded);
                const parsed = JSON.parse(decoded);
                console.log('ReportPrint: Parsed view filters from URL (after double decode):', parsed);
                return parsed;
            }
        } catch (e) {
            console.error('Failed to parse view filters from URL:', e, 'Raw param:', viewFiltersParam);
            return [];
        }
    }, [searchParams]);
    
    const [layouts, setLayouts] = useState({});
    const [report, setReport] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Track PDF render complete state - detects when all widgets finish loading
    const [isPdfRenderComplete, setIsPdfRenderComplete] = useState(false);
    const containerRef = React.useRef(null);
    
    // Monitor widget loading states for PDF generation
    useEffect(() => {
        if (isLoading || !report?.widgets?.length) return;
        
        const expectedWidgetCount = report.widgets.length;
        let checkCount = 0;
        const maxChecks = 150; // Safety limit: 150 * 200ms = 30 seconds max
        let stableCount = 0;
        const requiredStable = 3; // Must be stable for 3 consecutive checks
        
        const checkAllWidgetsLoaded = () => {
            const container = containerRef.current;
            if (!container) return false;
            
            // Find all widgets with loading state attribute
            const allWidgets = container.querySelectorAll('[data-widget-loading]');
            const loadingWidgets = container.querySelectorAll('[data-widget-loading="true"]');
            const loadedWidgets = container.querySelectorAll('[data-widget-loading="false"]');
            
            console.log(`PDF check: expected=${expectedWidgetCount}, rendered=${allWidgets.length}, loading=${loadingWidgets.length}, loaded=${loadedWidgets.length}`);
            
            // All expected widgets must be rendered AND none still loading
            return allWidgets.length >= expectedWidgetCount && 
                   loadingWidgets.length === 0 && 
                   loadedWidgets.length >= expectedWidgetCount;
        };
        
        const scheduleCheck = () => {
            checkCount++;
            if (checkCount > maxChecks) {
                console.log('PDF render: Max checks reached, proceeding anyway');
                setIsPdfRenderComplete(true);
                return;
            }
            
            if (checkAllWidgetsLoaded()) {
                stableCount++;
                console.log(`PDF render: Stable check ${stableCount}/${requiredStable}`);
                if (stableCount >= requiredStable) {
                    console.log('PDF render complete - all widgets loaded and stable');
                    setIsPdfRenderComplete(true);
                    return;
                }
            } else {
                stableCount = 0; // Reset if not all loaded
            }
            
            setTimeout(scheduleCheck, 200);
        };
        
        // Start checking after React has time to render widgets
        const timer = setTimeout(scheduleCheck, 500);
        return () => clearTimeout(timer);
    }, [isLoading, report?.widgets?.length]);

    // Process widgets for print - sort by layout position and filter hidden section breaks
    const processWidgetsForPrint = useCallback((layout, widgets) => {
        if (!layout || !widgets || widgets.length === 0) return [];
        
        // Use 'lg' layout as the base (or first available breakpoint)
        const breakpoint = layout.lg ? 'lg' : Object.keys(layout)[0];
        const layoutItems = layout[breakpoint] || [];
        
        // Create a map of widget data by ID
        const widgetMap = {};
        for (const widget of widgets) {
            widgetMap[widget.id] = widget;
        }
        
        // Create a map of layout info by widget ID
        const layoutMap = {};
        for (const item of layoutItems) {
            layoutMap[item.i] = item;
        }
        
        // Filter and sort widgets based on layout
        const sortedWidgets = [];
        
        for (const item of layoutItems) {
            const widget = widgetMap[item.i];
            if (!widget) continue;
            
            // Filter out section breaks without titles
            if (widget.type === 'sectionbreak') {
                const hasTitle = widget.config && widget.config.title && widget.config.title.trim() !== '';
                if (!hasTitle) continue;
            }
            
            sortedWidgets.push({
                widget,
                layout: item,
                // Sort key: row first (y), then column (x)
                sortKey: item.y * 1000 + item.x,
                // Mark if this widget should be full width
                isFullWidth: isFullWidthWidget(widget.type)
            });
        }
        
        // Sort by position (top to bottom, left to right)
        sortedWidgets.sort((a, b) => a.sortKey - b.sortKey);
        
        return sortedWidgets;
    }, []);

    // Load report configuration from backend
    const loadReport = useCallback(async () => {
        if (!keycloak.token) return;

        try {
            setIsLoading(true);

            const responseData = await authenticatedGet(`/api/reports/${id}`);
            
            // Extract report data from the success wrapper
            const reportData = responseData.success || responseData;
            
            // Ensure widgets is always an array
            const loadedReport = {
                ...reportData,
                widgets: reportData.widgets || []
            };
            
            setReport(loadedReport);
            setLayouts(loadedReport.layout || {});
        } catch (err) {
            console.error('Failed to load report:', err);
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, [keycloak.token, id]);

    // Initialize widget registry on mount
    useEffect(() => {
        // Clear registry to ensure fresh registration
        widgetRegistry.clear();
        
        // Force re-register all widget types to ensure latest sizes are used
        widgetRegistry.register('kpi', KPIWidget, { defaultSizes: getDefaultSizes('kpi') });
        widgetRegistry.register('table', TableWidget, { defaultSizes: getDefaultSizes('table') });
        widgetRegistry.register('areachart', AreaChartWidget, { defaultSizes: getDefaultSizes('areachart') });
        widgetRegistry.register('barchart', BarChartWidget, { defaultSizes: getDefaultSizes('barchart') });
        widgetRegistry.register('donutchart', DonutChartWidget, { defaultSizes: getDefaultSizes('donutchart') });
        widgetRegistry.register('sectionbreak', SectionBreakWidget, { defaultSizes: getDefaultSizes('sectionbreak') });
        widgetRegistry.register('forecastchart', ForecastChartWidget, { defaultSizes: getDefaultSizes('forecastchart') });
        widgetRegistry.register('forecasttable', ForecastTableWidget, { defaultSizes: getDefaultSizes('forecasttable') });
        widgetRegistry.register('resourcechangestrend', ResourceChangesTrendWidget, { defaultSizes: getDefaultSizes('resourcechangestrend') });
        widgetRegistry.register('resourcechangesoverview', ResourceChangesOverviewWidget, { defaultSizes: getDefaultSizes('resourcechangesoverview') });
        widgetRegistry.register('resourcelist', ResourceListWidget, { defaultSizes: getDefaultSizes('resourcelist') });
        widgetRegistry.register('resourceoverview', ResourceOverviewWidget, { defaultSizes: getDefaultSizes('resourceoverview') });
        widgetRegistry.register('resourceutilization', ResourceUtilizationWidget, { defaultSizes: getDefaultSizes('resourceutilization') });
        widgetRegistry.register('insighttable', InsightTableWidget, { defaultSizes: getDefaultSizes('insighttable') });
        widgetRegistry.register('costtrendchart', CostTrendChartWidget, { defaultSizes: getDefaultSizes('costtrendchart') });
    }, []);

    // Load report on mount
    useEffect(() => {
        loadReport();
    }, [loadReport]);

    // Add print-mode class to html element for CSS overrides
    useEffect(() => {
        document.documentElement.classList.add('print-mode');
        document.body.style.overflow = 'visible';
        document.body.style.height = 'auto';
        
        // Cleanup: remove class when component unmounts
        return () => {
            document.documentElement.classList.remove('print-mode');
            document.body.style.overflow = '';
            document.body.style.height = '';
        };
    }, []);

    // Update document title when report is loaded
    useEffect(() => {
        if (report && report.name) {
            document.title = `${report.name} - Print`;
        } else {
            document.title = 'Report - Print';
        }
        
        // Cleanup: reset title when component unmounts
        return () => {
            document.title = 'InstantViewAI';
        };
    }, [report]);

    // Render widget component (view-only, no edit controls)
    const renderWidget = (widget) => {
        const WidgetComponent = widgetRegistry.getComponent(widget.type);
        
        if (!WidgetComponent) {
            return (
                <div key={widget.id} className="bg-danger-soft border border-danger rounded-lg p-4">
                    <p className="text-danger">Unknown widget type: {widget.type}</p>
                </div>
            );
        }

        // Section breaks are rendered differently
        if (widget.type === 'sectionbreak') {
            return (
                <WidgetComponent
                    key={widget.id}
                    widgetId={widget.id}
                    config={widget.config}
                    isEditMode={false}
                />
            );
        }

        return (
            <WidgetComponent
                key={widget.id}
                widgetId={widget.id}
                config={widget.config}
                filters={[]}
                isEditMode={false}
            />
        );
    };

    // Format filter values for display
    const formatFilterValues = useCallback((values) => {
        if (!values || values.length === 0) return '';
        // Filter out null, undefined, and empty strings
        const validValues = values.filter(v => v != null && String(v).trim() !== '');
        if (validValues.length === 0) return '';
        if (validValues.length === 1) return String(validValues[0]);
        // For multiple values, show all if 5 or fewer, otherwise show first 3 and count
        if (validValues.length <= 5) {
            return validValues.join(', ');
        }
        return `${validValues.slice(0, 3).join(', ')}, and ${validValues.length - 3} more`;
    }, []);

    // Format filter category name for display
    const formatFilterCategory = useCallback((category, categoryDisplay) => {
        if (categoryDisplay) return categoryDisplay;
        if (!category) return '';
        // Convert snake_case or camelCase to Title Case
        return category
            .replaceAll('_', ' ')
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, str => str.toUpperCase())
            .trim();
    }, []);

    // Render filter block component
    const renderFilterBlock = useCallback(() => {
        if (!report) return null;

        const filters = report.filters || [];
        // Use view filters from URL parameters (passed during PDF generation) or from report data
        const viewFilters = viewFiltersFromURL.length > 0 ? viewFiltersFromURL : (report.view_filters || report.viewFilters || []);
        console.log('ReportPrint renderFilterBlock: viewFiltersFromURL:', viewFiltersFromURL);
        console.log('ReportPrint renderFilterBlock: viewFilters:', viewFilters);
        const hasReportFilters = filters.some(filter => {
            const values = filter.selectedValues || filter.selected_values || [];
            return values.length > 0;
        });
        const hasViewFilters = viewFilters.some(filter => {
            const values = filter.selectedValues || filter.selected_values || [];
            return values.length > 0;
        });
        console.log('ReportPrint renderFilterBlock: hasViewFilters:', hasViewFilters);

        return (
            <div className="report-print-filters">
                <h2 className="report-print-filters-title">Filters applied to this report</h2>
                <p className="report-print-filters-description">
                    The data in this report is shown with the following report-defined filters applied.
                </p>
                
                <div className="report-print-filters-content">
                    {/* Period filter */}
                    {startDate && endDate && (
                        <div className="report-print-filter-item">
                            <span className="report-print-filter-label">Period</span>
                            <span className="report-print-filter-value">
                                {startDate.format('MMM D, YYYY')} - {endDate.format('MMM D, YYYY')}
                            </span>
                        </div>
                    )}
                    
                    {/* Other filters */}
                    {hasReportFilters && filters.map((filter) => {
                        const categoryDisplay = formatFilterCategory(
                            filter.category || filter.category_display || '',
                            filter.categoryDisplay || filter.category_display
                        );
                        const values = filter.selectedValues || filter.selected_values || [];
                        const formattedValues = formatFilterValues(values);

                        if (!formattedValues) return null;

                        // Create a unique key from category and first value
                        const filterKey = `${categoryDisplay}-${values[0] || 'filter'}`;

                        return (
                            <div key={filterKey} className="report-print-filter-item">
                                <span className="report-print-filter-label">{categoryDisplay}</span>
                                <span className="report-print-filter-value">{formattedValues}</span>
                            </div>
                        );
                    })}
                </div>
                
                {/* Show message when no filters are applied - placed below grid */}
                {!hasReportFilters && (
                    <div className="report-print-no-filters-container">
                        <div className="report-print-no-filters-message">
                            No filters were applied
                        </div>
                        <div className="report-print-no-filters-explanation">
                            The report includes all available data for the selected time period.
                        </div>
                    </div>
                )}

                {/* View filters section */}
                {hasViewFilters && (
                    <>
                        <h3 className="report-print-view-filters-title">Viewer filters (applied at export)</h3>
                        <p className="report-print-view-filters-notice">
                            In addition to the report-defined filters, viewer-level filters were applied.
                        </p>
                        <div className="report-print-filters-content">
                            {viewFilters.map((filter) => {
                                const categoryDisplay = formatFilterCategory(
                                    filter.category || filter.category_display || '',
                                    filter.categoryDisplay || filter.category_display
                                );
                                const values = filter.selectedValues || filter.selected_values || [];
                                const formattedValues = formatFilterValues(values);

                                if (!formattedValues) return null;

                                // Create a unique key from category and first value
                                const filterKey = `view-${categoryDisplay}-${values[0] || 'filter'}`;

                                return (
                                    <div key={filterKey} className="report-print-filter-item">
                                        <span className="report-print-filter-label">{categoryDisplay}</span>
                                        <span className="report-print-filter-value">{formattedValues}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>
        );
    }, [report, startDate, endDate, viewFiltersFromURL, formatFilterValues, formatFilterCategory]);

    // Get processed widgets sorted by layout position
    const processedWidgets = useMemo(() => {
        if (!report || !report.widgets || !layouts) return [];
        return processWidgetsForPrint(layouts, report.widgets);
    }, [report, layouts, processWidgetsForPrint]);

    // Calculate widget height based on layout height and row height (60px)
    const getWidgetHeight = (layoutItem) => {
        const rowHeight = 60;
        const margin = 16;
        // Height = (h * rowHeight) + ((h - 1) * margin)
        return (layoutItem.h * rowHeight) + ((layoutItem.h - 1) * margin);
    };

    // Get grid column span (out of 12 columns)
    const getGridColumn = (layoutItem) => {
        const start = layoutItem.x + 1; // CSS grid is 1-indexed
        const end = start + layoutItem.w;
        return `${start} / ${end}`;
    };

    // Render report content based on state
    const renderReportContent = () => {
        if (isLoading) {
            return (
                <div className="flex items-center justify-center py-20">
                    <div className="flex items-center space-x-2">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-text"></div>
                        <span className="text-text-2">Loading report...</span>
                    </div>
                </div>
            );
        }

        if (error) {
            return (
                <div className="flex items-center justify-center py-20">
                    <div className="text-center">
                        <FontAwesomeIcon
                            icon={icon({ name: 'exclamation-triangle', family: 'sharp', style: 'light' })}
                            className="text-danger text-3xl mb-4"
                        />
                        <p className="text-danger text-lg mb-2">Failed to load report</p>
                        <p className="text-text-2 text-sm">{error}</p>
                    </div>
                </div>
            );
        }

        if (processedWidgets.length > 0) {
            // Group widgets into rows for print layout
            // Full-width widgets (charts, tables) get their own row
            // KPIs can share rows with other KPIs
            const rows = [];
            let currentKpiRow = [];

            for (const item of processedWidgets) {
                if (item.isFullWidth) {
                    // Full-width widget - flush any pending KPIs first
                    if (currentKpiRow.length > 0) {
                        rows.push({ type: 'kpi-row', items: currentKpiRow });
                        currentKpiRow = [];
                    }
                    // Add full-width widget as its own row
                    rows.push({ type: 'full-width', items: [item] });
                } else {
                    // KPI widget - can be grouped with other KPIs
                    currentKpiRow.push(item);
                }
            }
            
            // Don't forget any remaining KPIs
            if (currentKpiRow.length > 0) {
                rows.push({ type: 'kpi-row', items: currentKpiRow });
            }

            return (
                <div className="print-grid-container">
                    {rows.map((row, rowIndex) => (
                        <div 
                            key={`row-${row.items[0]?.widget?.id || rowIndex}`}
                            className="print-grid-row"
                            style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(12, 1fr)',
                                gap: '16px',
                                marginBottom: '16px'
                            }}
                        >
                            {row.items.map(({ widget, layout, isFullWidth }) => {
                                const height = getWidgetHeight(layout);
                                
                                return (
                                    <div
                                        key={widget.id}
                                        data-widget-id={widget.id}
                                        data-widget-type={widget.type}
                                        className="print-widget-container"
                                        style={{
                                            // Full-width widgets span all columns
                                            // KPIs use their original column layout
                                            gridColumn: isFullWidth ? '1 / -1' : getGridColumn(layout),
                                            minHeight: `${height}px`,
                                            height: 'auto', // Allow content to expand
                                            pageBreakInside: 'avoid',
                                            breakInside: 'avoid'
                                        }}
                                    >
                                        {renderWidget(widget)}
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            );
        }

        return (
            <div className="flex items-center justify-center py-20">
                <div className="text-center">
                    <FontAwesomeIcon
                        icon={icon({ name: 'chart-line', family: 'sharp', style: 'light' })}
                        className="text-text-2 text-3xl mb-4"
                    />
                    <p className="text-text-2 text-lg">No widgets configured</p>
                </div>
            </div>
        );
    };

    return (
        <div 
            ref={containerRef}
            className="report-print-container bg-white min-h-screen"
            data-pdf-render-complete={isPdfRenderComplete ? "true" : undefined}
        >
            {/* Print Header - appears on every page */}
            <div className="report-print-page-header">
                <div className="report-print-header-line"></div>
            </div>

            {/* Report Header - title and filter block */}
            {report && (
                <div className="report-print-header px-6 pt-6 pb-4 border-b border-border">
                    <h1 className="text-2xl font-semibold text-text mb-4">
                        {report.name}
                    </h1>
                    {renderFilterBlock()}
                </div>
            )}
            
            {/* Report Content */}
            <div className="report-print-content p-6">
                {renderReportContent()}
            </div>

            {/* Print Footer - appears on every page */}
            <div className="report-print-page-footer">
                <div className="report-print-footer-line"></div>
                <div className="report-print-footer-content">
                    <img src={logoWide} alt="InstantViewAI Logo" className="pt-3 report-print-footer-logo" />
                    <p className="report-print-footer-date pt-3 text-xs">
                        Generated on {new Date().toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                        })}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default ReportPrint;

